import { useRef, useState } from "react";
import axios from "axios";
import { Button } from "..";
import EmailCodeStep from "../EmailCodeStep/EmailCodeStep";
import "./AuthForm.scss";
import { CaptchaWidget } from "../CaptchaWidget/CaptchaWidget";
import { Check } from "lucide-react";
import {
  FORGOT_PASSWORD_EMAIL_CODE_URL,
  FORGOT_PASSWORD_RESET_URL,
} from "../../features/UrlHelper";

/*
 * Obnova hesla e-mailom (R25) -- jeden tok pre zakaznikov aj zamestnancov.
 *
 * Formular volal stary SMS tok (/forgot-password-request), ktory backend uz
 * nema, takze reset nefungoval nikomu. Teraz:
 *   1. "ziadost" -> prihlasovacie meno alebo e-mail + nove heslo + captcha
 *                   -> server posle 6-miestny kod na e-mail uctu
 *   2. "kod"     -> kod z e-mailu -> heslo sa zmeni
 *   3. "hotovo"
 *
 * Nove heslo sa zadava uz v prvom kroku (ako pri registracii), lebo kod sa
 * overuje az spolu so zmenou hesla -- samostatny krok "over kod" by nic
 * neoveril a zly kod by sa ukazal az po vypisani hesla.
 *
 * Server zamerne odpoveda rovnako pre neexistujuci ucet aj pre odoslany kod,
 * takze UI ide do kroku "kod" v oboch pripadoch. Jedina vynimka je ucet bez
 * e-mailu (`bezEmailu`) -- ten by na kod cakal zbytocne.
 */
const MIN_HESLO = 8;

function ForgotPasswordForm({ onBackToLogin, setCaptchaToken }) {
  const [krok, setKrok] = useState("ziadost");

  const [prihlasenie, setPrihlasenie] = useState("");
  const [noveHeslo, setNoveHeslo] = useState("");
  const [potvrdenie, setPotvrdenie] = useState("");

  // Turnstile token je jednorazovy -- po kazdom odoslani treba novy.
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);

  const [listok, setListok] = useState(null);
  const [napoveda, setNapoveda] = useState({ sprava: "", email: "", platnostMin: 15 });

  const [odosiela, setOdosiela] = useState(false);
  const [chyba, setChyba] = useState("");
  const [info, setInfo] = useState("");

  const nastavCaptchu = (token) => {
    setCaptcha(token);
    setCaptchaToken?.(token);
  };

  const resetCaptchy = () => {
    captchaRef.current?.reset?.();
    nastavCaptchu(null);
  };

  async function vyziadajKod() {
    const { data } = await axios.post(FORGOT_PASSWORD_EMAIL_CODE_URL, {
      login: prihlasenie.trim(),
      cfTurnstileToken: captcha,
    });
    resetCaptchy();
    return data;
  }

  // ── Krok 1: poslat kod ─────────────────────────────
  async function handleZiadost(e) {
    e.preventDefault();
    if (!prihlasenie.trim()) { setChyba("Zadajte prihlasovacie meno alebo e-mail."); return; }
    if (noveHeslo.length < MIN_HESLO) { setChyba(`Heslo musí mať aspoň ${MIN_HESLO} znakov.`); return; }
    if (noveHeslo !== potvrdenie) { setChyba("Heslá sa nezhodujú."); return; }
    if (!captcha) { setChyba("Počkajte na overenie, že nie ste robot."); return; }

    setOdosiela(true);
    setChyba("");
    setInfo("");
    try {
      const data = await vyziadajKod();
      if (data.bezEmailu) {
        setChyba(data.message);
        return;
      }
      setListok(data.listok || null);
      setNapoveda({
        sprava: data.message || "",
        email: data.emailNapoveda || "",
        platnostMin: data.platnostMin || 15,
      });
      setKrok("kod");
    } catch (err) {
      setChyba(err.response?.data?.message || "Kód sa nepodarilo odoslať. Skúste to znova.");
      resetCaptchy();
    } finally {
      setOdosiela(false);
    }
  }

  // ── Krok 2: kod + zmena hesla ──────────────────────
  async function handleKod(kod) {
    setOdosiela(true);
    setChyba("");
    setInfo("");
    try {
      await axios.post(FORGOT_PASSWORD_RESET_URL, {
        login: prihlasenie.trim(),
        emailListok: listok,
        emailKod: kod,
        newPassword: noveHeslo,
      });
      setKrok("hotovo");
    } catch (err) {
      setChyba(err.response?.data?.message || "Neplatný alebo expirovaný kód.");
    } finally {
      setOdosiela(false);
    }
  }

  async function handleZnova() {
    setChyba("");
    setInfo("");
    if (!captcha) {
      setChyba("Potvrďte overenie proti robotom nižšie a skúste to znova.");
      return;
    }
    setOdosiela(true);
    try {
      const data = await vyziadajKod();
      setListok(data.listok || null);
      setInfo("Poslali sme nový kód.");
    } catch (err) {
      setChyba(err.response?.data?.message || "Kód sa nepodarilo odoslať.");
      resetCaptchy();
    } finally {
      setOdosiela(false);
    }
  }

  // ── Krok 3: uspech ─────────────────────────────────
  if (krok === "hotovo") {
    return (
      <div className="auth-form auth-form--centered">
        <div className="auth-form__success-icon">
          <Check size={28} />
        </div>
        <p className="auth-form__success-text">
          Heslo bolo úspešne zmenené. Teraz sa môžete prihlásiť.
        </p>
        <Button variant="primary" style={{ width: "100%" }} onClick={onBackToLogin}>
          Prihlásiť sa
        </Button>
      </div>
    );
  }

  if (krok === "kod") {
    return (
      <div className="auth-form">
        <EmailCodeStep
          email={napoveda.email}
          popis={
            napoveda.email ? undefined : (
              <>{napoveda.sprava} Zadajte ho sem. Kód platí {napoveda.platnostMin} minút.</>
            )
          }
          platnostMin={napoveda.platnostMin}
          onSubmit={handleKod}
          onResend={handleZnova}
          onBack={() => { setKrok("ziadost"); setChyba(""); setInfo(""); }}
          backLabel="Upraviť údaje"
          submitLabel="Zmeniť heslo"
          chyba={chyba}
          info={info}
          odosiela={odosiela}
        />
        {/* Nova captcha pre "Poslať kód znova" -- predosly token uz je spotrebovany. */}
        <CaptchaWidget onVerify={nastavCaptchu} turnstileRef={captchaRef} />
      </div>
    );
  }

  return (
    <div className="auth-form">
      <form onSubmit={handleZiadost} noValidate>
        <p className="auth-form__hint">
          Zadajte prihlasovacie meno alebo e-mail a nové heslo. Na e-mail
          uložený vo vašom konte pošleme overovací kód.
        </p>
        <div className="auth-form__field">
          <label className="auth-form__label" htmlFor="forgot-login">
            Prihlasovacie meno alebo e-mail
          </label>
          <input
            id="forgot-login"
            type="text"
            className="auth-form__input"
            placeholder="Meno alebo e-mail"
            value={prihlasenie}
            onChange={(e) => setPrihlasenie(e.target.value)}
            autoComplete="username"
            autoFocus
          />
        </div>
        <div className="auth-form__field">
          <label className="auth-form__label" htmlFor="forgot-newpw">
            Nové heslo
          </label>
          <input
            id="forgot-newpw"
            type="password"
            className="auth-form__input"
            placeholder={`Minimálne ${MIN_HESLO} znakov`}
            value={noveHeslo}
            onChange={(e) => setNoveHeslo(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div className="auth-form__field">
          <label className="auth-form__label" htmlFor="forgot-confirmpw">
            Potvrdiť heslo
          </label>
          <input
            id="forgot-confirmpw"
            type="password"
            className="auth-form__input"
            placeholder="Zopakujte heslo"
            value={potvrdenie}
            onChange={(e) => setPotvrdenie(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <CaptchaWidget onVerify={nastavCaptchu} turnstileRef={captchaRef} />
        {chyba && <p className="auth-form__error">{chyba}</p>}
        <div className="auth-form__submit">
          <Button
            variant="primary"
            type="submit"
            style={{ width: "100%" }}
            disabled={odosiela}
          >
            {odosiela ? "Odosiela sa..." : "Poslať kód"}
          </Button>
        </div>
        {onBackToLogin && (
          <p className="auth-form__switch">
            Spomínate si na heslo?{" "}
            <button type="button" className="auth-form__link" onClick={onBackToLogin}>
              Prihlásiť sa
            </button>
          </p>
        )}
      </form>
    </div>
  );
}

export default ForgotPasswordForm;
